import Dexie from 'dexie';
import { Injectable } from '@angular/core';

@Injectable()
export class DatabaseService extends Dexie {

    cash_movement: Dexie.Table<CashMovement, string>;
    connected_account: Dexie.Table<ConnectedAccount, string>;
    country: Dexie.Table<Country, string>;
    courier: Dexie.Table<Courier, string>;
    custom_payment_service: Dexie.Table<CustomPaymentService, string>;
    event_category: Dexie.Table<EventCategory, string>;
    event_event: Dexie.Table<EventEvent, string>;
    event_event_category: Dexie.Table<EventEventCategory, string>;
    event_event_session: Dexie.Table<EventEventSession, string>;
    event_ticket_type: Dexie.Table<EventTicketType, string>;
    measurement_unit: Dexie.Table<MeasurementUnit, string>;
    organisation_organisation: Dexie.Table<Organisation, string>;
    parked_cart: Dexie.Table<ParkedCart, string>;
    pending_order: Dexie.Table<PendingOrder, string>;
    person_person: Dexie.Table<Person, string>;
    pricing_tier: Dexie.Table<PricingTier, string>;
    product_category: Dexie.Table<ProductCategory, string>;
    product_product_category: Dexie.Table<ProductProductCategory, string>;
    product_product: Dexie.Table<ProductProduct, string>;
    product_product_sub_product: Dexie.Table<ProductProductSubProduct, string>;
    register: Dexie.Table<Register, string>;
    supported_currency: Dexie.Table<SupportedCurrency, string>;
    tax_zone: Dexie.Table<TaxZone, string>;
    user: Dexie.Table<User, string>;

    constructor() {
        super('database');
        this.version(48).stores({
            cash_movement: 'id++, user_id, price, currency_id, date, note',
            connected_account: 'id, access_token, organisation_title, refresh_token, server_name, site_name, user_id, user',
            country: 'id, code, title, subdivisions',
            courier: 'id, title, tracking_url, country_id',
            custom_payment_service: 'id, name, status, pos_icon',
            event_category: 'id, order, title, parent_category_id',
            event_event_category: 'id, event_id, category_id',
            event_event: 'id, title, min_start_date, min_start_date_timezone, start_date, max_end_date, max_end_date_timezone, end_date, images, allow_sessions, allow_session_dates, allow_session_venues, timezone, venue, max_tickets, ticket_count, tickets_remaining, code, low_ticket_threshold',
            event_event_session: 'id, event_id, title, start_date, end_date, venue, timezone, max_tickets, ticket_count, tickets_remaining, order, low_ticket_threshold',
            event_ticket_type: 'id, event_id, event_session_id, title, tax_type, prices, max_tickets, ticket_count, tickets_remaining, order, low_ticket_threshold',
            measurement_unit: 'id, symbol',
            organisation_organisation:'id, title, phone_number, email_address, pricing_tier_id, allow_on_account_payment_method, has_contract_prices, amount_owing, credit_limit',
            parked_cart: 'id++, date, user_id, cart',
            pending_order: 'id++, idempotency_key, order_data, email_invoice_to_email_address, email_confirmation_to_email_address',
            person_person: 'id, first_name, last_name, full_name, job_title, allow_on_account_payment_method, number, email_address, mobile_phone_number, work_phone_number, home_phone_number, pricing_tier_id, organisation_id, amount_owing, credit_limit',
            pricing_tier: 'id, name, order, sellable, tax_inclusive, title',
            product_category: 'id, order, title, parent_category_id',
            product_product_category: 'id, product_id, category_id',
            product_product: 'id, title, allow_sub_products, low_available_stock_level, prices, available_stock, track_stock, tax_type, weighting, sku, barcode, image, measurement_unit_id, quantity_decimal_places, default_quantity, quantity_step_interval',
            product_product_sub_product: 'id, product_id, sku, barcode, low_available_stock_level, available_stock, track_stock, prices, tax_type, variations, product_image, measurement_unit_id, quantity_decimal_places, default_quantity, quantity_step_interval',
            register: 'id, title, default_pricing_tier, location, require_pin_when_switching_user',
            supported_currency: 'id, code, base, sign, title, cash_rounding_mode',
            tax_zone: 'id, country, tax_rate_4, tax_rate_5',
            user: 'id, first_name, last_name, username, email_address, pin_hash, photo, timezone'
        });
    }
}

export interface CashMovement {
    id?: string;
    user_id?: string;
    price?: number;
    currency_id?: string;
    date?: string;
    note?: string;
}

export interface ConnectedAccount {
    id?: string;
    access_token?: string;
    organisation_title?: string;
    refresh_token?: string;
    server_name?: string;
    site_name?: string;
    // TEMPORARY 30 JUL 2025, v2.8.5
    user?: [];
    user_id?: string;
}

export interface Country {
    id?: string;
    code?: string;
    title?: string;
    subdivisions?: [];
}

export interface Courier {
    id?: string;
    title?: string;
    tracking_url?: string;
    country_id?: string;
}

export interface CustomPaymentService {
    id?: string;
    name?: string;
    status?: boolean;
    pos_icon?: string;
}

export interface EventCategory {
    id?: string;
    order: number;
    title?: [];
    parent_category_id?: string;
}

export interface EventEventCategory {
    event_id?: string;
    category_id?: string;
}

export interface EventEvent {
    id?: string;
    title?: string;
    min_start_date?: Date;
    min_start_date_timezone?: [];
    start_date?: Date;
    max_end_date?: Date;
    max_end_date_timezone?: [];
    end_date?: Date;
    images?: [];
    allow_sessions?: boolean;
    allow_session_dates?: boolean;
    allow_session_venues?: boolean;
    timezone?: [];
    venue?: [];
    max_tickets?: number;
    ticket_count?: number;
    tickets_remaining?: number;
    code?: string;
    low_ticket_threshold?: number;
}

export interface EventEventSession {
    id?: string;
    event_id?: string;
    title?: string;
    start_date?: Date;
    end_date?: Date;
    venue?: string;
    timezone?: [];
    max_tickets?: number;
    ticket_count?: number;
    tickets_remaining?: number;
    order?: number;
    low_ticket_threshold?: number;
}

export interface EventTicketType {
    id?: string;
    event_id?: string;
    event_session_id?: string;
    title?: string;
    tax_type?: string;
    prices?: [];
    max_tickets?: number;
    ticket_count?: number;
    tickets_remaining?: number;
    order?: number;
    low_ticket_threshold?: number;
}

export interface MeasurementUnit {
    id?: string;
    symbol?: string;
}

export interface Organisation {
    id?: string;
    title?: string;
    phone_number?: string;
    email_address?: string;
    pricing_tier_id?: string;
    allow_on_account_payment_method?: boolean;
    has_contract_prices?: boolean;
    amount_owing?: number;
    credit_limit?: number;
}

export interface ParkedCart {
    date?: Date;
    user_id?: string;
    cart?: [];
}

export interface PendingOrder {
    order_data?: [];
    idempotency_key?: string;
    email_invoice_to_email_address?: string;
    email_confirmation_to_email_address?: string;
}

export interface Person {
    id?: string;
    first_name?: string;
    last_name?: string;
    full_name?: string;
    job_title?: string;
    allow_on_account_payment_method?: boolean;
    number?: number;
    email_address?: string;
    mobile_phone_number?: string;
    work_phone_number?: string;
    home_phone_number?: string;
    pricing_tier_id?: string;
    organisation_id?: string;
    amount_owing?: number;
    credit_limit?: number;
}

export interface PricingTier {
    id?: string;
    name?: string;
    order?: number;
    sellable?: boolean;
    tax_inclusive?: boolean;
    title?: string;
}

export interface ProductCategory {
    id?: string;
    order?: number;
    title?: [];
    parent_category_id?: string;
}

export interface ProductProductCategory {
    product_id?: string;
    category_id?: string;
}

export interface ProductProduct {
    id?: string;
    title?: string;
    allow_sub_products?: boolean;
    low_available_stock_level?: number;
    prices?: [];
    available_stock?: number;
    track_stock?: boolean;
    tax_type?: string;
    weighting?: number;
    sku?: string;
    barcode?: string;
    quantity_decimal_places?: number; 
    default_quantity?: number;
    quantity_step_interval?: number;
    image?: [];
}

export interface ProductProductSubProduct {
    id?: string;
    product_id?: string;
    sku?: string;
    barcode?: string;
    low_available_stock_level?: number;
    available_stock?: number;
    track_stock?: boolean;
    prices?: [];
    tax_type?: number;
    quantity_decimal_places?: number; 
    default_quantity?: number;
    quantity_step_interval?: number;    
    variations?: [];
    product_image?: [];
}

export interface Register {
    id?: string;
    title?: string;
    default_pricing_tier?: [];
    location?: [];
    require_pin_when_switching_user?: boolean;
}

export interface SupportedCurrency {
    id?: string;
    code?: string;
    base?: boolean;
    sign?: string;
    title?: string;
    cash_rounding_mode?: number;
}

export interface TaxZone {
    id?: string;
    country?: [];
    tax_rate_4?: number;
    tax_rate_5?: number;
}

export interface User {
    id?: string;
    first_name?: string;
    last_name?: string;
    username?: string;
    email_address?: string;
    pin_hash?: string;
    photo?: [];
    timezone?: [];
}